import { HelpCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  FEATURE_MANUAL_SLUG,
  type ManualFeatureKey,
} from '../../constants/manual.constants';
import { manualSectionPath } from '../../helpers/manualPath';
import './manual-link.scss';

interface Props {
  /**
   * A semantic FEATURE key (not a raw slug). Resolves to a manual slug via the
   * feature→slug map so the link can never drift from its manual page.
   */
  section: ManualFeatureKey;
  /** Optional in-page sub-section anchor (`/readme/<slug>#<sub>`). */
  sub?: string;
  /** Optional visible label; icon-only when omitted. */
  label?: string;
  className?: string;
}

/**
 * Reusable help icon that deep-links to the in-app User Manual (TRIL-424).
 * Rendered at the top of feature pages whose mechanics need explaining.
 */
export default function ManualLink({ section, sub, label, className }: Props) {
  const slug = FEATURE_MANUAL_SLUG[section];
  const to = manualSectionPath(slug, sub);

  // Icon-only: give it an accessible name + a hover tooltip. With a visible
  // label, the label already names it — a duplicate title tooltip is noise.
  return (
    <Link
      to={to}
      className={`MANUAL__LINK${className ? ` ${className}` : ''}`}
      aria-label={label ? undefined : 'How this works — open the manual'}
      title={label ? undefined : 'How this works'}
    >
      <HelpCircle size={16} aria-hidden="true" />
      {label && <span className="MANUAL__LINK__LABEL">{label}</span>}
    </Link>
  );
}
